import React, { useMemo } from 'react';
import { BaseComponentImpl } from '../../../types/implementations';
import { Building } from '../../../types/protocols';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';
import ListView from './ListView';
import { GroupingStrategy } from '../GenericComponentListPage';
import { asSatelliteMarkers, partitionForView, satelliteDrawingView } from '../../../Helpers/componentDrawing';

interface MapViewProps {
    components: BaseComponentImpl[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    hoveredKey: string | null;
    onComponentClick: (key: string) => void;
    onComponentHover: (key: string | null) => void;
    groupingStrategy: GroupingStrategy;
}

const MapView: React.FC<MapViewProps> = ({
    components,
    building,
    buildingKey,
    selectedKey,
    hoveredKey,
    onComponentClick,
    onComponentHover,
    groupingStrategy,
}) => {
    const mapImageKey = building.model.currentMapImageKey;
    const mapImage = mapImageKey ? building.model.mapImages?.[mapImageKey] : undefined;

    // Pinned components are drawn (shadowed so BlueprintWithMarkers finds the
    // pin under blueprintLocations); the ones that belong on the satellite
    // image but carry no pin are listed — see componentDrawing.
    const { wrappedComponents, unlocatedComponents } = useMemo(() => {
        const view = satelliteDrawingView(building.model);
        if (!view?.imageKey) {
            return { wrappedComponents: [], unlocatedComponents: components };
        }
        const { placed, unplaced } = partitionForView(components, view, building.model.structure);
        return { wrappedComponents: asSatelliteMarkers(placed, view.imageKey), unlocatedComponents: unplaced };
    }, [components, building]);

    if (!mapImage || !mapImageKey) {
        return (
            <div>
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#999',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                }}>
                    <p style={{ margin: 0 }}>Ingen kartbild tillgänglig</p>
                </div>

                {components.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                        <ListView
                            components={components}
                            building={building}
                            buildingKey={buildingKey}
                            selectedKey={selectedKey}
                            onComponentClick={onComponentClick}
                            groupingStrategy={groupingStrategy}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <BlueprintWithMarkers
                blueprint={mapImage}
                currentBlueprintKey={mapImageKey}
                building={building}
                utilities={wrappedComponents}
                selectedUtilityKey={selectedKey}
                onUtilityClick={onComponentClick}
                hideTitle
                compactMarkers
            />

            {unlocatedComponents.length > 0 && (
                <div>
                    <h4 className="generic-component-section-header">
                        Inte på kartan:
                    </h4>
                    <ListView
                        components={unlocatedComponents}
                        building={building}
                        buildingKey={buildingKey}
                        selectedKey={selectedKey}
                        onComponentClick={onComponentClick}
                        groupingStrategy="none"
                    />
                </div>
            )}
        </div>
    );
};

export default MapView;
